import React, { useEffect, useMemo, useState } from "react";
import { Minus, Plus, Trash2 } from "lucide-react";


const FIELD_OPTIONS = {
  dosage: ["½ tablet", "1 tablet", "2 tablets", "1 capsule", "2 capsules", "5 mL", "10 mL", "1 puff", "2 puffs", "Apply thin layer", "As directed"],
  frequency: ["Once daily", "Twice daily", "Three times daily", "Four times daily", "Every 6 hours", "Every 8 hours", "At bedtime", "As needed"],
  duration: ["3 days", "5 days", "7 days", "10 days", "14 days", "1 month", "Until review", "As needed"],
  timing: ["After food", "Before food", "With food", "Any time", "At bedtime"],
};

const FIELD_LABELS = {
  dosage: "Dose each time",
  frequency: "How often",
  duration: "For how long",
  timing: "When to take",
};

const DOSE_FACTORS = {
  "½ tablet": { amount: 0.5, kind: "solid" },
  "1 tablet": { amount: 1, kind: "solid" },
  "2 tablets": { amount: 2, kind: "solid" },
  "1 capsule": { amount: 1, kind: "solid" },
  "2 capsules": { amount: 2, kind: "solid" },
  "5 mL": { amount: 5, kind: "liquid" },
  "10 mL": { amount: 10, kind: "liquid" },
  "1 puff": { amount: 1, kind: "puff" },
  "2 puffs": { amount: 2, kind: "puff" },
};

const FREQUENCY_FACTORS = {
  "Once daily": 1,
  "Twice daily": 2,
  "Three times daily": 3,
  "Four times daily": 4,
  "Every 6 hours": 4,
  "Every 8 hours": 3,
  "At bedtime": 1,
};

function durationInDays(duration) {
  if (duration === "1 month") return 30;
  const match = /^(\d+) days$/.exec(duration || "");
  return match ? Number(match[1]) : null;
}

function unitSupportsDose(kind, unitName) {
  const unit = (unitName || "").trim().toLowerCase();
  if (!unit) return kind === "solid";
  if (kind === "liquid") return /(^|\W)ml($|\W)|millilitre|milliliter/.test(unit);
  if (kind === "puff") return /puff|dose/.test(unit);
  return !/bottle|ampou?le|vial|sachet|tube|cream|ointment|(^|\W)ml($|\W)|lit(re|er)/.test(unit);
}

function formatQuantity(value) {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(3)));
}

export function calculateDispenseQuantity({ dosage, frequency, duration, pharmacy_unit_name: unitName }) {
  const dose = DOSE_FACTORS[dosage];
  const timesPerDay = FREQUENCY_FACTORS[frequency];
  const days = durationInDays(duration);
  if (!dose || !timesPerDay || !days || !unitSupportsDose(dose.kind, unitName)) return null;

  const quantity = dose.amount * timesPerDay * days;
  return {
    quantity: formatQuantity(quantity),
    explanation: `${dosage} × ${timesPerDay} ${timesPerDay === 1 ? "time" : "times"}/day × ${days} days`,
  };
}

function GuidedChoice({ field, value, onChange }) {
  const choices = FIELD_OPTIONS[field];
  const [custom, setCustom] = useState(Boolean(value && !choices.includes(value)));

  useEffect(() => {
    if (value && !choices.includes(value)) setCustom(true);
    if (choices.includes(value)) setCustom(false);
  }, [choices, value]);

  const selectValue = custom ? "__custom" : (value || "");
  return <label className="text-xs font-semibold text-slate-600">
    {FIELD_LABELS[field]}
    <select
      aria-label={FIELD_LABELS[field]}
      className="mg-input mt-1 w-full bg-white py-2 text-sm"
      value={selectValue}
      onChange={(event) => {
        if (event.target.value === "__custom") {
          setCustom(true);
          onChange("");
        } else {
          setCustom(false);
          onChange(event.target.value);
        }
      }}
    >
      <option value="">Choose</option>
      {choices.map((choice) => <option key={choice} value={choice}>{choice}</option>)}
      <option value="__custom">Other…</option>
    </select>
    {custom && <input
      autoFocus
      aria-label={`Custom ${FIELD_LABELS[field].toLowerCase()}`}
      className="mg-input mt-2 w-full py-2 text-sm"
      placeholder={`Enter ${FIELD_LABELS[field].toLowerCase()}`}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
    />}
  </label>;
}

function DispenseQuantity({ item, onChange, suggestion, manuallyEdited, onUseSuggestion }) {
  const unit = item.pharmacy_unit_name || "base units";
  const numeric = Number(item.pharmacy_quantity || 0);
  const available = Number(item.pharmacy_available_quantity);
  const availableLabel = Number.isFinite(available) ? available.toLocaleString() : item.pharmacy_available_quantity;
  const adjust = (amount) => {
    const next = Math.max(1, (Number.isFinite(numeric) ? numeric : 1) + amount);
    onChange(String(next), true);
  };
  return <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 sm:col-span-2">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div>
        <label htmlFor={`dispense-${item.draft_id}`} className="text-sm font-semibold text-mg-navy">Total quantity to dispense</label>
        <p className="mt-0.5 text-xs text-slate-600">Enter the total number of {unit} the pharmacy should provide.</p>
        {item.pharmacy_lot_number && <p className="mt-1 text-xs font-semibold text-blue-700">Lot {item.pharmacy_lot_number}{item.pharmacy_expiry_date ? ` · Expires ${item.pharmacy_expiry_date}` : ""}</p>}
      </div>
      {item.pharmacy_available_quantity != null && <span className="rounded-full bg-white px-2 py-1 text-xs text-slate-600">Available: {availableLabel} {unit}</span>}
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <button type="button" aria-label="Decrease dispense quantity" className="rounded-lg border border-blue-200 bg-white p-2 text-mg-navy" onClick={() => adjust(-1)}><Minus className="h-4 w-4" /></button>
      <div className="relative w-36">
        <input
          id={`dispense-${item.draft_id}`}
          aria-label={`Dispense quantity in ${unit}`}
          type="number"
          min="0.000001"
          step="1"
          className="mg-input w-full py-2 pr-14 text-center text-base font-semibold"
          value={item.pharmacy_quantity || ""}
          onChange={(event) => onChange(event.target.value, true)}
        />
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-slate-500">{unit}</span>
      </div>
      <button type="button" aria-label="Increase dispense quantity" className="rounded-lg border border-blue-200 bg-white p-2 text-mg-navy" onClick={() => adjust(1)}><Plus className="h-4 w-4" /></button>
      <div className="flex flex-wrap gap-1 pl-1" aria-label="Common dispense quantities">
        {[5, 10, 15, 30].map((quantity) => <button key={quantity} type="button" className="rounded-full border border-blue-200 bg-white px-2.5 py-1 text-xs font-semibold text-mg-medical" onClick={() => onChange(String(quantity), true)}>{quantity}</button>)}
      </div>
    </div>
    {suggestion
      ? <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-xs text-slate-600">
        <span>{manuallyEdited ? "Edited manually. " : "Suggested from directions: "}{suggestion.explanation} = <strong>{suggestion.quantity} {unit}</strong>. You can edit it.</span>
        {manuallyEdited && <button type="button" className="font-semibold text-mg-medical hover:underline" onClick={onUseSuggestion}>Use suggested {suggestion.quantity}</button>}
      </div>
      : <p className="mt-2 text-xs text-slate-500">Quantity cannot be calculated safely from these directions and this stock unit. Enter it manually.</p>}
  </div>;
}

export function PrescriptionLineEditor({ item, index, medicineEditor, onChange, onRemove }) {
  const pharmacyItem = Boolean(item.pharmacy_product_selection_id);
  const [quantityManuallyEdited, setQuantityManuallyEdited] = useState(false);
  const { dosage, frequency, duration, pharmacy_unit_name } = item;
  const quantitySuggestion = useMemo(() => calculateDispenseQuantity({ dosage, frequency, duration, pharmacy_unit_name }), [dosage, frequency, duration, pharmacy_unit_name]);

  useEffect(() => {
    setQuantityManuallyEdited(false);
  }, [item.draft_id]);

  useEffect(() => {
    if (!pharmacyItem || quantityManuallyEdited || !quantitySuggestion) return;
    if (String(item.pharmacy_quantity || "") !== quantitySuggestion.quantity) {
      onChange("pharmacy_quantity", quantitySuggestion.quantity);
    }
  }, [item.pharmacy_quantity, onChange, pharmacyItem, quantityManuallyEdited, quantitySuggestion]);

  const changeQuantity = (value, manual = false) => {
    if (manual) setQuantityManuallyEdited(true);
    onChange("pharmacy_quantity", value);
  };

  const useSuggestedQuantity = () => {
    if (!quantitySuggestion) return;
    setQuantityManuallyEdited(false);
    onChange("pharmacy_quantity", quantitySuggestion.quantity);
  };

  return <fieldset className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid={`prescription-line-${index + 1}`}>
    <legend className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Medicine {index + 1}</legend>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <label className="text-xs font-semibold text-slate-600">Medicine</label>
        {pharmacyItem
          ? <div className="mt-1 rounded-lg border border-blue-200 bg-white px-3 py-2"><span className="font-semibold text-mg-navy">{item.medicine_name}</span><span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">Pharmacy item</span></div>
          : <div className="mt-1">{medicineEditor}</div>}
      </div>
      <button type="button" aria-label={`Remove medicine ${index + 1}`} onClick={onRemove} className="mt-5 rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
    </div>

    <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <label className="text-xs font-semibold text-slate-600">Strength
        <input aria-label={`Medicine ${index + 1} strength`} className="mg-input mt-1 w-full py-2 text-sm" placeholder="Example: 500 mg" value={item.strength || ""} onChange={(event) => onChange("strength", event.target.value)} />
      </label>
      {Object.keys(FIELD_OPTIONS).map((field) => <GuidedChoice key={field} field={field} value={item[field] || ""} onChange={(value) => onChange(field, value)} />)}
      <label className="text-xs font-semibold text-slate-600 sm:col-span-2 lg:col-span-3">Extra instructions
        <input aria-label={`Medicine ${index + 1} instructions`} className="mg-input mt-1 w-full py-2 text-sm" placeholder="Optional notes for the patient or pharmacist" value={item.instructions || ""} onChange={(event) => onChange("instructions", event.target.value)} />
      </label>
      {pharmacyItem && <DispenseQuantity item={item} onChange={changeQuantity} suggestion={quantitySuggestion} manuallyEdited={quantityManuallyEdited} onUseSuggestion={useSuggestedQuantity} />}
    </div>
  </fieldset>;
}
